import { tcsoClient } from '../../../api/atomProxyClient';
import { buildPostRequest } from '../../../api/apiClient';

export interface TcsoRunConfig {
  identifier: string;
  chfDeviceId: string;
  gpfDeviceId: string;
  newChfId: string;
  newGpfId: string;
  chfLocation: string;
  aerialInstall: string;
  signal?: AbortSignal;
}

export type TcsoRunOutcome =
  | { outcome: 'SUCCESS' | 'FAIL' | 'TIMEOUT' | 'CANCELLED' }
  | { outcome: 'FIELD_ERRORS'; chfError: string; gpfError: string };

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs one TCSO end-to-end: L1 generate-xml, an initial wait, then L2 get-xml polling
 * until a final status or until the retry budget is exhausted (-> TIMEOUT).
 * Pure request/response logic: no React state, no context, no reporting. Never rejects.
 *
 * The error semantics deliberately mirror the elec-only journey (TcsoStartScreen), which
 * this runner will eventually replace: a 422 from L1 carries per-field GUID errors, any
 * other L1 failure is FAIL without polling, and a single 500 from L2 ends the run with
 * FAIL — no retry, unlike the comms check runners. One intentional delta: an unrecognised
 * L2 status counts against the retry budget and ends in TIMEOUT, where the elec journey
 * silently stops polling and hangs on the waiting screen forever.
 */
export async function runTcso(config: TcsoRunConfig): Promise<TcsoRunOutcome> {
  const initialTimeoutMs = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_INITIAL_TIMEOUT}`, 10);
  const pollTimeoutMs = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_TIMEOUT}`, 10);
  const maxRetries = parseInt(`${process.env.REACT_APP_TCSO_GET_XML_MAX_RETRIES}`, 10);

  if (config.signal?.aborted) {
    return { outcome: 'CANCELLED' };
  }

  // Same payload for elec-only and dual fuel — TCSO swaps a comms hub, not a meter.
  // The key asymmetry (newChfId vs newGpfDeviceId) is what the lambda expects.
  const request = {
    identifier: config.identifier,
    deviceType: 'CHF',
    chfDeviceId: config.chfDeviceId,
    gpfDeviceId: config.gpfDeviceId,
    newChfId: config.newChfId,
    newGpfDeviceId: config.newGpfId,
    chfLocation: config.chfLocation,
    aerialInstall: config.aerialInstall,
  };

  let l1Response: any;
  try {
    const httpResponse = await tcsoClient.generateXml(buildPostRequest(request));
    if (httpResponse.status === 422) {
      const guidErrors: { chfError?: string | null; gpfError?: string | null } = await httpResponse.json();
      return { outcome: 'FIELD_ERRORS', chfError: guidErrors.chfError ?? '', gpfError: guidErrors.gpfError ?? '' };
    }
    if (httpResponse.status >= 400) {
      return { outcome: 'FAIL' };
    }
    l1Response = await httpResponse.json();
  } catch {
    return { outcome: 'FAIL' };
  }

  await delay(initialTimeoutMs);

  // `maxRetries > retries` matches the elec journey and is NaN-safe: with the env var
  // unset the comparison is false and the run times out after a single poll.
  for (let retries = 0; ; retries += 1) {
    if (retries > 0) {
      await delay(pollTimeoutMs);
    }
    if (config.signal?.aborted) {
      return { outcome: 'CANCELLED' };
    }
    try {
      // The poll handle is the whole L1 body, not an .atomL1response field — the TCSO
      // get-xml lambda differs from the comms check ones here.
      const httpResponse = await tcsoClient.getXml(buildPostRequest(l1Response));
      if (httpResponse.status === 500) {
        return { outcome: 'FAIL' };
      }
      const l2Response = await httpResponse.json();
      if (l2Response.status === 'FAIL') {
        return { outcome: 'FAIL' };
      }
      if (l2Response.status === 'SUCCESS') {
        return { outcome: 'SUCCESS' };
      }
      // PROCESSING (or anything unrecognised) counts against the retry budget
      if (!(maxRetries > retries)) {
        return { outcome: 'TIMEOUT' };
      }
    } catch {
      return { outcome: 'FAIL' };
    }
  }
}
